import { useEffect } from "react";
import { useActiveDisplay } from "@/desktop/use-active-display";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { isTauri } from "@/lib/is-tauri";
import { selectProjectByIdentity } from "@/lib/select-project-space";
import {
  captureTaskOpenIntent,
  openTaskDetails,
} from "@/lib/right-panel-navigation";
import { useWorkToastStore } from "@/stores/work-toast-store";

/** Blocked pills ask for a task through the mirrored store because the toast
 *  stack has no `useLayout()` in the compact chrome (spec §6.4). */
export function useOpenTaskRequest(): void {
  const request = useWorkToastStore((state) => state.openTaskRequest);
  // Reactive, not `useActiveDisplay.getState()` read once inside the effect:
  // a pending request must be re-evaluated when the active display changes,
  // or the window that should consume it never gets another chance to.
  const activeDisplayId = useActiveDisplay((state) => state.activeDisplayId);

  useEffect(() => {
    if (!request) return;
    // The stack already brought the workspace forward on the cursor's display.
    if (isTauri() && getWindowDisplayId() !== activeDisplayId) return;
    const consumed = useWorkToastStore.getState().consumeOpenTaskRequest();
    if (!consumed) return;
    const shouldContinue = captureTaskOpenIntent();
    void selectProjectByIdentity(
      { projectId: consumed.projectId },
      { shouldContinue },
    ).then((selected) => {
      if (!selected || !shouldContinue()) return;
      void openTaskDetails(consumed.taskId, { shouldContinue });
    });
  }, [activeDisplayId, request]);
}
